import type { ContactSummary } from '@chatbot/shared-types';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AppShell } from '../components/AppShell.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Skeleton } from '../components/Skeleton.tsx';
import { toast } from '../components/Toast.tsx';
import { StateBadge } from '../components/conversations/StateBadge.tsx';
import { useTenantContext } from '../hooks/useTenantContext.ts';
import { contactsApi } from '../lib/contacts-api.ts';

const CHANNEL_FILTERS: Array<{ label: string; value: 'all' | 'telegram' | 'whatsapp' }> = [
  { label: 'Todos', value: 'all' },
  { label: 'Telegram', value: 'telegram' },
  { label: 'WhatsApp', value: 'whatsapp' },
];

export function ContactsListPage() {
  const { activeTenantId, tenantIds } = useTenantContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const channelFilter = (searchParams.get('channel') as 'telegram' | 'whatsapp' | null) ?? 'all';
  const tenantFilter = searchParams.get('tenantId') ?? activeTenantId ?? tenantIds[0] ?? '';
  const searchFilter = searchParams.get('search') ?? '';

  const [items, setItems] = useState<ContactSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!tenantFilter) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await contactsApi.list({
        tenantId: tenantFilter,
        channel: channelFilter === 'all' ? undefined : channelFilter,
        search: searchFilter || undefined,
        limit: 20,
      });
      setItems(res.items);
      setCursor(res.cursor);
    } catch (err) {
      setError((err as Error).message);
      toast.error(`Error al cargar contactos: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [tenantFilter, channelFilter, searchFilter]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  async function loadMore() {
    if (!cursor || !tenantFilter) return;
    setLoadingMore(true);
    try {
      const res = await contactsApi.list({
        tenantId: tenantFilter,
        channel: channelFilter === 'all' ? undefined : channelFilter,
        search: searchFilter || undefined,
        limit: 20,
        cursor,
      });
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.cursor);
    } catch (err) {
      toast.error(`Error al cargar más contactos: ${(err as Error).message}`);
    } finally {
      setLoadingMore(false);
    }
  }

  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  return (
    <AppShell>
      <div className="flex flex-col h-full bg-surface-base border border-edge-subtle rounded-lg shadow-sm">
        <header className="px-6 py-4 border-b border-edge-subtle flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-content-primary">Contactos</h1>
            {tenantIds.length > 1 ? (
              <div className="mt-1 flex items-center gap-2">
                <span className="text-sm text-content-tertiary">Tenant:</span>
                <select
                  value={tenantFilter}
                  onChange={(e) => setFilter('tenantId', e.target.value)}
                  className="select-native py-1 px-2 border border-edge rounded bg-surface-base text-sm focus:outline-none"
                >
                  {tenantIds.map((tid) => (
                    <option key={tid} value={tid}>
                      {tid}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <p className="text-sm text-content-tertiary">
                {tenantFilter ? `Tenant: ${tenantFilter}` : 'Selecciona un tenant'}
              </p>
            )}
          </div>
        </header>

        <div className="px-6 py-3 border-b border-edge-subtle flex gap-2 flex-wrap items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {CHANNEL_FILTERS.map((f) => {
              const isActive =
                channelFilter === f.value || (f.value === 'all' && !searchParams.get('channel'));
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setFilter('channel', f.value === 'all' ? null : f.value)}
                  aria-pressed={isActive}
                  className={`px-3 py-1 rounded-full text-sm font-semibold transition ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'bg-surface-sunken text-content-secondary hover:bg-neutral-200 dark:hover:bg-neutral-700'
                  }`}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <input
            type="search"
            placeholder="Buscar por nombre o identificador…"
            value={searchFilter}
            onChange={(e) => setFilter('search', e.target.value)}
            className="input input-sm max-w-xs"
            aria-label="Buscar contactos"
          />
        </div>

        <div className="flex-1 overflow-auto">
          {loading && items.length === 0 ? (
            <div className="p-6">
              <Skeleton rows={5} />
            </div>
          ) : error ? (
            <div className="p-6 text-center text-content-tertiary flex flex-col items-center justify-center gap-4">
              <p className="text-danger-600 font-semibold">
                No se pudieron cargar los contactos: {error}
              </p>
              <button
                type="button"
                onClick={refetch}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded font-medium shadow-sm transition"
              >
                Reintentar
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="p-12 text-center text-content-tertiary">
              <EmptyState
                message={
                  searchFilter
                    ? `Sin resultados para "${searchFilter}".`
                    : 'Aún no hay contactos registrados para este tenant.'
                }
              />
            </div>
          ) : (
            <>
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {items.map((c) => (
                  <li
                    key={`${c.tenantId}-${c.userId}`}
                    className="px-6 py-4 hover:bg-surface-sunken dark:hover:bg-neutral-850 cursor-pointer flex items-center gap-4 group transition-colors"
                    onClick={() =>
                      navigate(`/contacts/${c.tenantId}/${encodeURIComponent(c.userId)}`)
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        navigate(`/contacts/${c.tenantId}/${encodeURIComponent(c.userId)}`);
                      }
                    }}
                  >
                    <Avatar name={c.userDisplayName ?? c.userIdentifier ?? c.userId} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-semibold text-content-primary truncate group-hover:text-primary-600 transition-colors">
                          {c.userDisplayName || c.userIdentifier || c.userId}
                        </span>
                        {c.channel && (
                          <span className="text-xs px-1.5 py-0.5 rounded bg-surface-sunken text-content-secondary font-medium uppercase font-mono">
                            {c.channel}
                          </span>
                        )}
                        {c.currentState && <StateBadge state={c.currentState} />}
                      </div>
                      {c.userIdentifier && c.userDisplayName && (
                        <p className="text-xs text-content-tertiary font-mono truncate">
                          {c.userIdentifier}
                        </p>
                      )}
                      {c.lastMessagePreview && (
                        <p className="text-sm text-content-secondary truncate">
                          {c.lastMessagePreview}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      {c.assignedAdminName && (
                        <span className="text-xs text-content-tertiary font-medium">
                          por {c.assignedAdminName}
                        </span>
                      )}
                      <time className="text-xs text-content-secondary font-mono">
                        {relativeTime(c.lastMessageAt ?? c.lastActivity)}
                      </time>
                    </div>
                  </li>
                ))}
              </ul>
              {cursor && (
                <div className="p-4 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="px-4 py-2 bg-surface-sunken hover:bg-neutral-200 dark:hover:bg-neutral-700 text-content-secondary rounded font-medium transition disabled:opacity-50"
                  >
                    {loadingMore ? 'Cargando…' : 'Cargar más'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function Avatar({ name }: { name: string }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="h-10 w-10 shrink-0 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center font-semibold text-sm">
      {initials || '?'}
    </div>
  );
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}
